import {Controller} from "@hotwired/stimulus"
import {EditorView, basicSetup} from "codemirror"
import {Compartment} from "@codemirror/state"
import {sql, StandardSQL} from "@codemirror/lang-sql"

export default class extends Controller {
  static targets = ["editor", "input"]
  static values = {
    doc: String,
    dialect: {type: String, default: "duckdb"},
    options: {type: Array, default: []},
    readOnly: {type: Boolean, default: false}
  }

  connect() {
    this.languageCompartment = new Compartment()

    this.editor = new EditorView({
      doc: this.docValue,
      extensions: [
        basicSetup,
        EditorView.editable.of(!this.readOnlyValue),
        this.languageCompartment.of(sql()), // Start with basic SQL
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            this.inputTarget.value = update.state.doc.toString()
          }
        }),
        EditorView.theme({
          "&": {height: "100%", minHeight: "200px"},
          ".cm-scroller": {overflow: "auto"},
          // Ensure it looks like a form input
          "&.cm-focused": {outline: "none"}
        })
      ],
      parent: this.editorTarget
    })

    // Ensure input is synced initially
    this.inputTarget.value = this.editor.state.doc.toString()

    // Load completions
    this.loadAndSetCompletions()
  }

  async loadAndSetCompletions() {
    const orgMetaElement = document.querySelector('meta[name="organizationSlug"]')
    const organizationSlug = orgMetaElement ? orgMetaElement.content : document.querySelector('[data-id="org-value"]')?.value
    const dialect = this.dialectValue

    if (!organizationSlug) {
      console.error("Could not determine organization slug for autocompletion.")
      return
    }

    try {
      const url = new URL(`/${organizationSlug}/auto_completions`, window.location.origin)
      url.searchParams.append("dialect", dialect)

      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Network response was not ok. Status: ${response.status}`)
      }

      const allCompletions = await response.json()

      // Transform flat list [{table: "t", name: "c"}, ...] into schema object { "t": ["c", ...] }
      const schema = {}

      allCompletions.forEach(item => {
        if (!schema[item.table]) {
          schema[item.table] = []
        }
        schema[item.table].push(item.name)
      })

      // Update the editor with the schema-aware SQL dialect
      this.editor.dispatch({
        effects: this.languageCompartment.reconfigure(sql({
          dialect: StandardSQL,
          schema: schema
        }))
      })

    } catch (error) {
      console.error("Error fetching autocompletions:", error)
    }
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  }
}
